import { TypewriterEffectSmooth } from '@/components/shadcn/typewriter-effect'
import { useRestClient } from '@/contexts/QueryProvider'
import { motion } from 'motion/react'
import { useEffect, useMemo, useRef, useState } from 'react'

export function BenefitsTypewriter() {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [typewriterWidth, setTypewriterWidth] = useState<number>(0)
  const [animationKey, setAnimationKey] = useState(0)
  const { getLandingApiGetPlatformExternal } = useRestClient()
  const { data } = getLandingApiGetPlatformExternal.useSuspenseQuery(undefined)
  const typewriterRef = useRef<HTMLDivElement>(null)
  const measureRef = useRef<HTMLDivElement>(null)

  const filteredItems = useMemo(
    () =>
      data?.filter((item) => item.card.groupings.some((grouping) => ['Benefits', 'Our edge'].includes(grouping.value))),
    [data]
  )
  const transformedItems = useMemo(
    () =>
      filteredItems?.map((item) => ({
        description: item.card.description,
        subtitle: item.card.subtitle,
        title: item.card.title.split(' ').map((word) => ({
          text: word,
        })),
      })),
    [filteredItems]
  )

  const length = transformedItems?.length ?? 0

  // Effect to cycle through items
  useEffect(() => {
    if (length === 0) return
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => {
        const newIndex = (prevIndex + 1) % length
        setAnimationKey((prev) => prev + 1) // Force animation restart
        return newIndex
      })
    }, 10000)
    return () => clearInterval(interval)
  }, [length])

  // Measure the text width before animation starts
  useEffect(() => {
    if (!transformedItems?.[currentIndex] || !measureRef.current) return

    // Measure the full text width using a hidden element
    const textContent = transformedItems[currentIndex].title.map((word) => word.text).join(' ')
    measureRef.current.textContent = textContent
    const width = measureRef.current.getBoundingClientRect().width
    setTypewriterWidth(width)
  }, [currentIndex, transformedItems])

  return (
    <motion.div
      key={`container-${animationKey}`}
      initial={{ opacity: 1 }}
      animate={{ opacity: 0 }}
      transition={{ duration: 0.4, delay: 9.6 }}
      className="flex h-[209px] flex-col items-center gap-y-3"
    >
      {/* Hidden element for measuring text width */}
      <div
        ref={measureRef}
        className="invisible absolute text-xs font-bold sm:text-base md:text-xl lg:text-3xl xl:text-5xl"
        style={{ whiteSpace: 'nowrap' }}
      />

      <motion.p
        key={`subtitle-${animationKey}`}
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 1.5 }}
        style={{
          // height: 64,
          width: typewriterWidth > 0 ? `${typewriterWidth}px` : 'auto',
          textAlign: 'center',
        }}
        // className="text-balance pb-2 text-xs font-light italic !leading-tight text-muted-foreground dark:text-neutral-200 sm:text-lg"
        className="text-balance pb-2 text-xs font-light !leading-tight text-muted-foreground dark:text-neutral-200 sm:text-lg"
      >
        {transformedItems?.[currentIndex].subtitle}
      </motion.p>

      <div
        key={`typewriter-${animationKey}`}
        style={{ width: typewriterWidth > 0 ? `${typewriterWidth}px` : 'auto' }}
        className="flex justify-center"
      >
        <TypewriterEffectSmooth
          cursorClassName="bg-primary/50 translate-y-0.5 h-3"
          // className="border-y-2 border-primary/20 pt-1"
          className="rounded-xl bg-primary/10 px-5 pt-1"
          ref={typewriterRef}
          words={transformedItems?.[currentIndex].title ?? []}
        />
      </div>

      <motion.p
        key={`description-${animationKey}`}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 1.8 }}
        style={{
          // height: 48,
          width: typewriterWidth > 0 ? `${typewriterWidth}px` : 'auto',
          textAlign: 'center',
        }}
        className="-translate-x-1 text-balance pt-3 text-xs !leading-tight text-muted-foreground dark:text-neutral-200 sm:text-base"
      >
        {transformedItems?.[currentIndex].description}
      </motion.p>
    </motion.div>
  )
}
